/**
 * OAuth2 服务 - Web 端使用 Authorization Code + PKCE
 */

import endpoints from './endpoints';

const OAUTH_CONFIG = {
  clientId: import.meta.env.VITE_OAUTH_CLIENT_ID || 'oneauth-web',
  redirectUri: import.meta.env.VITE_OAUTH_REDIRECT_URI || 'http://localhost:3000/callback',
  scope: import.meta.env.VITE_OAUTH_SCOPE || 'openid profile email offline',
  authorizeUrl: import.meta.env.VITE_HYDRA_AUTHORIZE_URL || 'http://localhost:4444/oauth2/auth',
  tokenUrl: import.meta.env.VITE_HYDRA_TOKEN_URL || 'http://localhost:4444/oauth2/token',
  revokeUrl: import.meta.env.VITE_HYDRA_REVOKE_URL || 'http://localhost:4444/oauth2/revoke',
}

// ==================== PKCE 工具函数 ====================

/**
 * 生成随机字符串
 */
export function generateRandomString(length = 64) {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~'
  const randomValues = new Uint8Array(length)
  crypto.getRandomValues(randomValues)
  return Array.from(randomValues)
    .map(v => charset[v % charset.length])
    .join('')
}

/**
 * 生成 code_challenge
 */
export async function generateCodeChallenge(codeVerifier) {
  const encoder = new TextEncoder()
  const data = encoder.encode(codeVerifier)
  const digest = await crypto.subtle.digest('SHA-256', data)

  // Base64 URL 编码
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

// ==================== OAuth2 流程 ====================

/**
 * 1. 发起 OAuth2 授权请求 (跳转)
 * @param {string} returnUrl - 登录成功后返回的 URL
 */
export async function initiateOAuth2Login(returnUrl = '/') {
  try {
    const authorizeUrl = await getAuthorizeUrl(returnUrl);

    console.log('🚀 Initiating OAuth2 login (redirect):', authorizeUrl);

    // 4. 跳转到授权页面
    window.location.href = authorizeUrl;

  } catch (error) {
    console.error('❌ Failed to initiate OAuth2 login:', error);
    throw error;
  }
}

/**
 * 获取 OAuth2 授权 URL (不跳转)
 * @param {string} returnUrl - 登录成功后返回的 URL
 */
export async function getAuthorizeUrl(returnUrl = '/') {
  // 1. 生成 PKCE 参数
  const codeVerifier = generateRandomString(64);
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = generateRandomString(32);

  // 2. 保存 PKCE 参数
  // - sessionStorage: 用于重定向登录（同一标签页）
  // - localStorage: 用于 Popup 登录（跨窗口，sessionStorage 不共享）
  sessionStorage.setItem('oauth_code_verifier', codeVerifier);
  sessionStorage.setItem('oauth_state', state);
  sessionStorage.setItem('oauth_return_url', returnUrl);
  localStorage.setItem('oauth_code_verifier', codeVerifier);
  localStorage.setItem('oauth_state', state);
  localStorage.setItem('oauth_return_url', returnUrl);

  // 3. 构造授权 URL
  const params = new URLSearchParams({
    client_id: OAUTH_CONFIG.clientId,
    redirect_uri: OAUTH_CONFIG.redirectUri,
    response_type: 'code',
    scope: OAUTH_CONFIG.scope,
    state: state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    // UI定制
    login_methods: ['password','phone'],
    enable_social:false,
    default_method: 'phone',
    require_username: false,
    verify_method: 'phone',
    confirm_password: false,
    require_name: false
  });

  return `${OAUTH_CONFIG.authorizeUrl}?${params.toString()}`;
}

/**
 * 打开 OAuth2 登录小窗 (Popup)
 * @param {string} returnUrl - 登录成功后返回的 URL
 */
export async function openLoginPopup(returnUrl = '/') {
  try {
    const authorizeUrl = await getAuthorizeUrl(returnUrl);

    console.log('🚀 Opening OAuth2 login popup:', authorizeUrl);

    // 计算屏幕居中位置
    const width = 500;
    const height = 600;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    window.open(
      authorizeUrl,
      'oauth2_login_popup',
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
    );

  } catch (error) {
    console.error('❌ Failed to open OAuth2 login popup:', error);
    throw error;
  }
}

/**
 * 2. 处理 OAuth2 回调
 * @param {string} code - 授权码
 * @param {string} state - 状态参数
 * @returns {Promise<Object>} Token 响应
 */
export async function handleOAuth2Callback(code, state) {
  try {
    console.log('📥 Handling OAuth2 callback:', {
      code: code?.substring(0, 20) + '...',
      state: state?.substring(0, 10) + '...',
    })

    // 1. 验证 state (防止 CSRF 攻击)
    const sessionState = sessionStorage.getItem('oauth_state')
    const localState = localStorage.getItem('oauth_state')
    const stateMatches = (sessionState && state === sessionState) || (localState && state === localState)
    if (!stateMatches) {
      throw new Error('Invalid state parameter - possible CSRF attack')
    }

    // 2. 获取 code_verifier (PKCE)
    const codeVerifier = sessionStorage.getItem('oauth_code_verifier') || localStorage.getItem('oauth_code_verifier')
    if (!codeVerifier) {
      throw new Error('Missing code_verifier - PKCE flow requires code_verifier')
    }

    // 3. 直接调用 Hydra Token Endpoint 交换 Token (Public Client 模式)
    console.log('🔄 Exchanging code for token via Hydra (PKCE)...')

    // 使用 exchangeCodeForToken 函数 (直接请求 Hydra)
    const tokenData = await exchangeCodeForToken(code, codeVerifier)

    console.log('📦 Token exchange successful:', {
      has_access_token: !!tokenData.access_token,
      expires_in: tokenData.expires_in
    })

    // 4. 解析 ID Token 获取用户信息
    let user = null
    if (tokenData.id_token) {
      try {
        user = parseIdToken(tokenData.id_token)
        console.log('👤 Parsed user from ID Token:', user)
      } catch (e) {
        console.warn('Failed to parse ID Token:', e)
      }
    }

    // 尝试从后端获取完整用户信息 (可选)
    // if (tokenData.access_token) {
    //   try {
    //      user = await fetchUserInfo(tokenData.access_token);
    //   } catch(e) { console.warn('Failed to fetch user info', e); }
    // }

    // 构造返回数据（存储由 authService.saveAuth 统一管理）
    const resultData = {
      access_token: tokenData.access_token,
      refresh_token: tokenData.refresh_token,
      id_token: tokenData.id_token,
      expires_in: tokenData.expires_in,
      scope: tokenData.scope,
      token_type: tokenData.token_type,
      user: user
    }

    // 5. 清理 sessionStorage 中的 PKCE 数据
    sessionStorage.removeItem('oauth_code_verifier')
    sessionStorage.removeItem('oauth_state')
    localStorage.removeItem('oauth_code_verifier')
    localStorage.removeItem('oauth_state')

    console.log('✅ OAuth2 authentication complete')

    return resultData

  } catch (error) {
    console.error('❌ OAuth2 callback failed:', error)
    throw error
  }
}

/**
 * 解析 ID Token (JWT)
 * @param {string} idToken - ID Token (JWT格式)
 * @returns {Object} 用户信息
 */
function parseIdToken(idToken) {
  try {
    // JWT 格式: header.payload.signature
    const parts = idToken.split('.')
    if (parts.length !== 3) {
      throw new Error('Invalid ID token format')
    }

    // Base64URL 解码 payload
    const payload = parts[1]
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )

    const decoded = JSON.parse(jsonPayload)

    // 提取用户信息（字段名依据 OneAuth/Hydra 的 ID Token 结构）
    return {
      id: decoded.sub, // subject (用户 ID)
      email: decoded.email,
      name: decoded.name,
      username: decoded.preferred_username || decoded.email,
      phone: decoded.phone_number,
      email_verified: decoded.email_verified,
      phone_verified: decoded.phone_number_verified,
    }
  } catch (error) {
    console.error('Failed to parse ID token:', error)
    throw new Error('Invalid ID token')
  }
}

/**
 * 用授权码交换 Token
 */
async function exchangeCodeForToken(code, codeVerifier) {
  const params = new URLSearchParams({
    grant_type: 'authorization_code',
    code: code,
    redirect_uri: OAUTH_CONFIG.redirectUri,
    client_id: OAUTH_CONFIG.clientId,
    code_verifier: codeVerifier,
  })

  const response = await fetch(OAUTH_CONFIG.tokenUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    credentials: 'include', // 发送 Cookie（跨域场景）
    body: params.toString(),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error_description || `HTTP ${response.status}`)
  }

  const data = await response.json()

  return {
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    id_token: data.id_token,
    expires_in: data.expires_in,
    scope: data.scope,
    token_type: data.token_type,
  }
}

/**
 * 3. 刷新 Access Token (通过 OneAuth 统一服务)
 * @param {string} refreshToken - Refresh Token
 * @returns {Promise<Object>} 新的 Token
 */
export async function refreshAccessToken(refreshToken) {
  try {
    console.log('🔄 Refreshing access token via OneAuth unified service...')

    // ✅ 通过 OneAuth 统一服务刷新 token (所有产品统一使用)
    const ONEAUTH_URL = import.meta.env.VITE_ONEAUTH_URL || 'http://localhost:8080'
    const refreshUrl = `${ONEAUTH_URL}${endpoints.oneauth.oauth.refresh}`

    const response = await fetch(refreshUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        refresh_token: refreshToken,
        client_id: OAUTH_CONFIG.clientId,
      }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error?.message || errorData.message || 'Token refresh failed')
    }

    const data = await response.json()

    console.log('✅ Token refreshed successfully via OneAuth')

    return {
      access_token: data.access_token,
      refresh_token: data.refresh_token || refreshToken, // 可能返回新的 refresh_token
      expires_in: data.expires_in,
      scope: data.scope,
      token_type: data.token_type || 'Bearer',
    }

  } catch (error) {
    console.error('❌ Token refresh failed:', error)
    throw error
  }
}

/**
 * 4. 撤销 Token (登出) - 已废弃
 * @deprecated 请使用 logoutWithOneAuth() 统一登出
 * @param {string} token - Access Token 或 Refresh Token
 */
export async function revokeToken(token) {
  console.warn('revokeToken is deprecated. Please use logoutWithOneAuth() instead.')
  try {
    console.log('🔒 Revoking token...')

    const params = new URLSearchParams({
      token: token,
      client_id: OAUTH_CONFIG.clientId,
    })

    const response = await fetch(OAUTH_CONFIG.revokeUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    })

    // Revoke 接口可能返回 200 或 204
    if (!response.ok && response.status !== 204) {
      console.warn('Token revoke returned:', response.status)
    }

    console.log('✅ Token revoked successfully')

  } catch (error) {
    console.error('❌ Token revoke failed:', error)
    // 不抛出错误,即使撤销失败也继续登出流程
  }
}

/**
 * 5. 撤销 OAuth2 Tokens (POST 请求，安全)
 * @param {string} accessToken - Access Token
 * @param {string} refreshToken - Refresh Token
 */
export async function revokeOAuth2Tokens(accessToken, refreshToken) {
  try {
    console.log('🔒 Revoking OAuth2 tokens...')

    const ONEAUTH_URL = import.meta.env.VITE_ONEAUTH_URL || 'http://localhost:8080'

    const response = await fetch(`${ONEAUTH_URL}${endpoints.oneauth.oauth.logout}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        access_token: accessToken || '',
        refresh_token: refreshToken || '',
      }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.warn('⚠️ Token revocation failed:', errorData)
    } else {
      console.log('✅ OAuth2 tokens revoked successfully')
    }
  } catch (error) {
    console.error('❌ Token revocation error:', error)
    // 不抛出错误，继续登出流程
  }
}

/**
 * 6. 重定向登出 (撤销 Kratos Session)
 * @param {string} postLogoutRedirectUri - 登出后重定向地址
 */
export function redirectToLogout(postLogoutRedirectUri) {
  console.log('🚪 Redirecting to OneAuth logout...')

  const ONEAUTH_URL = import.meta.env.VITE_ONEAUTH_URL || 'http://localhost:8080'
  const logoutUrl = new URL(`${ONEAUTH_URL}/api/auth/logout`)

  // 设置登出后重定向地址
  logoutUrl.searchParams.set('post_logout_redirect_uri', postLogoutRedirectUri || window.location.origin)

  console.log('🔗 Redirecting to:', logoutUrl.toString())

  // 重定向到 OneAuth 登出页面（撤销 Kratos session）
  window.location.href = logoutUrl.toString()
}

/**
 * 7. 统一登出 (两步流程)
 * 1. POST 撤销 OAuth2 tokens（安全，不暴露在 URL）
 * 2. 重定向撤销 Kratos session
 *
 * @param {string} accessToken - Access Token (可选)
 * @param {string} refreshToken - Refresh Token (可选)
 * @param {string} postLogoutRedirectUri - 登出后重定向地址 (默认当前页面)
 */
export async function logoutWithOneAuth(accessToken, refreshToken, postLogoutRedirectUri) {
  // 1. POST 撤销 OAuth2 tokens
  await revokeOAuth2Tokens(accessToken, refreshToken)

  // 2. 重定向撤销 Kratos session
  redirectToLogout(postLogoutRedirectUri)
}

/**
 * 获取返回 URL
 */
export function getOAuthReturnUrl() {
  const url = sessionStorage.getItem('oauth_return_url') || localStorage.getItem('oauth_return_url') || '/'
  sessionStorage.removeItem('oauth_return_url')
  localStorage.removeItem('oauth_return_url')
  return url
}

export default {
  initiateOAuth2Login,
  getAuthorizeUrl,
  openLoginPopup,
  handleOAuth2Callback,
  refreshAccessToken,
  revokeToken, // deprecated
  revokeOAuth2Tokens,
  redirectToLogout,
  logoutWithOneAuth,
  getOAuthReturnUrl,
}
